<script setup lang="ts">
  import { NavbarItem } from "@/types/Navbar.ts"
  import { NavigationMenuContent, NavigationMenuTrigger } from "reka-ui"
  import { computed, ref, watchEffect } from "vue"
  import NestedSubitem from "@/components/navbar/items/NestedSubitem.vue"
  import { Button } from "@/components/ui/button"
  import { Link } from "@/components/ui/link"

  const props = defineProps<{
    triggerLabel: string
    subItems: NavbarItem[]
    itemClicked: () => void
    parentOpened: boolean
  }>()

  const openNestedItems = ref(true)

  const navMenuId = computed(
    () => `${props.triggerLabel.toLowerCase()}-nav-menu-content`,
  )

  watchEffect(() => {
    openNestedItems.value = props.parentOpened
  })
</script>

<template>
  <NavigationMenuTrigger as-child>
    <Button
      variant="text"
      :data-testid="`${triggerLabel}-trigger`"
      :aria-controls="openNestedItems ? navMenuId : undefined"
    >
      {{ triggerLabel }}
    </Button>
  </NavigationMenuTrigger>
  <NavigationMenuContent
    :id="navMenuId"
    class="top-2 rounded-md border-[1px] border-secondary-300 bg-secondary-100 shadow-md dark:border-secondary-500 dark:bg-secondary-500"
    :data-testid="`${triggerLabel}-submenu-content`"
  >
    <div class="flex min-w-[325px] flex-col space-y-2 p-4">
      <template
        v-for="({ to, label, children, isDisabled }, index) in props.subItems"
      >
        <Button
          v-if="to && !isDisabled"
          :key="index"
          variant="navSubItem"
          size="nav"
          as-child
          class="group w-full justify-start rounded-full pl-4"
          :data-testid="`${label}-submenu-nav-item`"
          @click="props.itemClicked"
        >
          <Link :to="to">{{ label }}</Link>
        </Button>

        <NestedSubitem
          v-if="children?.length && openNestedItems"
          :key="index"
          :sub-items="children"
          :trigger-label="label"
          :item-clicked="props.itemClicked"
        />
      </template>
    </div>
  </NavigationMenuContent>
</template>
